import { z } from 'zod';
import { apiGet } from '@/lib/api';

/**
 * RAPORTY SKONFIGUROWANE W SPS (13.10.2026) — podgląd do odtworzenia raportów u nas. Dane składa API
 * (`raporty-sps.ts`); tu schemat i opisy po ludzku (wyzwalacze Harmonogramu, makra dat SPS).
 *
 * ⚠️ ŁAGODNIE (`passthrough`, `nullish`): to podgląd danych z maszyn klienta — nowe pole z agenta
 * nie może wywrócić całej zakładki.
 */
const Uruchomienie = z
  .object({
    wezel: z.string(),
    maszyna: z.string().nullish(),
    zadanie: z.string(),
    sciezka: z.string().nullish(),
    wlaczone: z.boolean(),
    stan: z.string().nullish(),
    tryb: z.string().nullish(),
    raporty: z.array(z.string()),
    parametry: z.array(z.string()),
    wyzwalacze: z.array(z.record(z.string(), z.unknown())),
    ostatnio: z.string().nullish(),
    wynik: z.number().nullish(),
    nastepne: z.string().nullish(),
    konto: z.string().nullish(),
  })
  .passthrough();
export type UruchomienieSps = z.infer<typeof Uruchomienie>;

const RaportSps = z
  .object({
    nazwa: z.string(),
    bazowy: z.object({ id: z.string(), nazwa: z.string().nullish(), kategoria: z.string().nullish() }),
    naglowek: z.string().nullish(),
    formatPliku: z.string().nullish(),
    dataWNazwie: z.boolean(),
    zbiorczy: z.boolean(),
    tylkoSumy: z.boolean(),
    topN: z.object({ pole: z.string().nullish(), ile: z.number().nullish() }).nullish(),
    kolumny: z.array(z.object({ pole: z.string(), podpis: z.string().nullish(), grupuj: z.boolean(), suma: z.boolean(), wyrazenie: z.string().nullish() })),
    warunki: z.array(
      z.object({
        pole: z.string(),
        podpis: z.string().nullish(),
        regula: z.string().nullish(),
        wartosc: z.string().nullish(),
        od: z.string().nullish(),
        do: z.string().nullish(),
      }),
    ),
    wysylka: z.array(
      z
        .object({
          szablon: z.string(),
          format: z.string().nullish(),
          znany: z.boolean(),
          rodzaj: z.string().nullish(),
          do: z.string().nullish(),
          dw: z.string().nullish(),
          temat: z.string().nullish(),
          katalog: z.string().nullish(),
          ftp: z.string().nullish(),
        })
        .passthrough(),
    ),
    uruchomienia: z.array(Uruchomienie),
    /** Maszyny, w których bazie raport istnieje (13.10.2026); `takSamo: false` — tam inna wersja. */
    skonfigurowanyNa: z
      .array(z.object({ wezel: z.string(), maszyna: z.string().nullish(), takSamo: z.boolean() }))
      .default([]),
  })
  .passthrough();
export type RaportSpsWidok = z.infer<typeof RaportSps>;

export const RaportySpsSchema = z
  .object({
    zrodlo: z.object({ wezel: z.string(), maszyna: z.string().nullish(), zgloszonoO: z.string() }).nullable(),
    raporty: z.array(RaportSps),
    zadaniaBezRaportu: z.array(Uruchomienie),
    wezly: z.array(
      z.object({
        wezel: z.string(),
        maszyna: z.string().nullish(),
        zgloszonoO: z.string(),
        konfiguracja: z.boolean(),
        zadan: z.number().nullable(),
        bledy: z.array(z.string()),
      }),
    ),
  })
  .passthrough();
export type RaportySps = z.infer<typeof RaportySpsSchema>;

export const fetchRaportySps = (groupId: string) => apiGet(`/reports/sps?groupId=${groupId}`, RaportySpsSchema);

type T = (k: string) => string;

/** Godzina z `StartBoundary` tak, jak ją wpisano w Harmonogramie — bez przeliczania stref. */
const godzina = (start: unknown): string => {
  const m = /T(\d{2}:\d{2})/.exec(typeof start === 'string' ? start : '');
  return m ? m[1]! : '';
};
const lista = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);
const DNI = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * WYZWALACZ HARMONOGRAMU PO LUDZKU — z pól, które agent wyjmuje z XML-a zadania
 * (`ScheduleByDay/Week/Month/MonthDayOfWeek`, `TimeTrigger`, powtarzanie).
 */
export function opisWyzwalacza(w: Record<string, unknown>, t: T): string {
  const o = godzina(w.start);
  const przy = o ? ` ${t('rsps.o')} ${o}` : '';
  const dni = lista(w.dniTygodnia)
    .sort((a, b) => DNI.indexOf(a) - DNI.indexOf(b))
    .map((d) => t(`rsps.dzien.${d}`));
  const miesiace = lista(w.miesiace);
  const wybraneMiesiace = miesiace.length > 0 && miesiace.length < 12 ? ` (${miesiace.map((m) => t(`rsps.miesiac.${m}`)).join(', ')})` : '';
  const powtarzaj = typeof w.powtarzaj === 'string' && w.powtarzaj !== '' ? ` · ${t('rsps.powtarzaj')} ${czasIso(w.powtarzaj)}` : '';
  let opis: string;
  if (w.rodzaj === 'TimeTrigger') opis = `${t('rsps.jednorazowo')} ${String(w.start ?? '').replace('T', ' ').slice(0, 16)}`;
  else if (w.rodzaj === 'LogonTrigger') opis = t('rsps.przyLogowaniu');
  else if (w.rodzaj === 'BootTrigger') opis = t('rsps.przyStarcie');
  else if (w.coIleDni) opis = (w.coIleDni === '1' ? t('rsps.codziennie') : t('rsps.coDni').replace('{n}', String(w.coIleDni))) + przy;
  else if (w.coIleTygodni)
    opis = (w.coIleTygodni === '1' ? t('rsps.coTydzien') : t('rsps.coTygodni').replace('{n}', String(w.coIleTygodni))) + (dni.length ? ` (${dni.join(', ')})` : '') + przy;
  else if (lista(w.dniMiesiaca).length > 0)
    opis = t('rsps.coMiesiacDnia').replace('{dni}', lista(w.dniMiesiaca).join(', ')) + wybraneMiesiace + przy;
  else if (lista(w.tygodnieMiesiaca).length > 0)
    opis = t('rsps.coMiesiacTygodnia').replace('{tygodnie}', lista(w.tygodnieMiesiaca).join(', ')).replace('{dni}', dni.join(', ')) + wybraneMiesiace + przy;
  else opis = `${String(w.rodzaj ?? '?')}${przy}`;
  return opis + powtarzaj;
}

/** `PT1H30M` → `1 h 30 min` — tylko to, co Harmonogram realnie zapisuje w powtarzaniu. */
export function czasIso(v: string): string {
  const m = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/.exec(v);
  if (m === null) return v;
  return [m[1] ? `${m[1]} d` : '', m[2] ? `${m[2]} h` : '', m[3] ? `${m[3]} min` : ''].filter(Boolean).join(' ') || v;
}

/** Makro daty SPS (`[start of last month]`) po polsku; nieznane zostaje w oryginale. */
export function makroDaty(v: string, t: T): string {
  return v.replace(/\[([a-z ]+)\]/gi, (calosc, nazwa: string) => {
    const k = `rsps.makro.${nazwa.toLowerCase().replace(/\s+/g, '')}`;
    const tr = t(k);
    return tr === k ? calosc : tr;
  });
}
